import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useUserPermissions } from 'auth/hooks';
import { AccessPermissions } from 'core/enums';
import { FormProvider } from 'core/states';
import { useAccessActions } from 'pricing/hooks';
import { IPricingProfileAccessItem } from 'pricing/types';

import { upsertPricingProfileAccessSchema } from './schema';
import { IPricingProfileAccessForm } from './types';
import { UpsertPricingProfileAccessFormFields } from './UpsertPricingProfileAccessFormFields';
import { mapAccessItemToFormData, mapUpsertPricingProfileAccess } from './utils';

interface IProps {
  pricingProfileAccessItem?: IPricingProfileAccessItem;
  pricingProfileId: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  onRemoveSuccess?: () => void;
}

export const UpsertPricingProfileAccess = ({
  pricingProfileAccessItem,
  pricingProfileId,
  isOpen,
  onClose,
  onSubmitSuccess,
  onRemoveSuccess,
}: IProps) => {
  const { upsertAccess } = useAccessActions();
  const { hasPermission } = useUserPermissions();

  const hasAllowAllAccessPermission = useMemo(
    () => hasPermission(AccessPermissions.ACCESS_PRICING_PROFILE_ALLOW_ALL_ACCESS),
    [hasPermission]
  );

  const defaultValues = useMemo(() => {
    if (pricingProfileAccessItem) {
      return mapAccessItemToFormData(pricingProfileAccessItem);
    }

    return {
      pricingProfileId,
      allCustomers: false,
      allUsers: true,
      allLocations: true,
      allRegions: true,
      customers: [
        {
          customerContactGroup: null,
          customerLocations: [],
        },
      ],
      users: [],
    };
  }, [pricingProfileAccessItem, pricingProfileId]);

  const onSubmit =
    (reset: UseFormReset<IPricingProfileAccessForm>) => async (data: IPricingProfileAccessForm) => {
      const mappedData = mapUpsertPricingProfileAccess(data, pricingProfileAccessItem);
      const { success } = await upsertAccess(mappedData);

      if (!success) {
        return;
      }

      setTimeout(() => {
        onSubmitSuccess();
        handleOnClose(reset);
      }, 1000);
    };

  const handleOnRemoveSuccess = () => {
    setTimeout(() => {
      onRemoveSuccess?.();
      handleOnClose();
    }, 1000);
  };

  const handleOnClose = (reset?: UseFormReset<IPricingProfileAccessForm>) => {
    onClose();
    reset?.(defaultValues);
  };

  return (
    <FormProvider
      schema={upsertPricingProfileAccessSchema({ hasAllowAllAccessPermission })}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertPricingProfileAccessFormFields
        pricingProfileAccessItem={pricingProfileAccessItem}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        onRemoveSuccess={handleOnRemoveSuccess}
      />
    </FormProvider>
  );
};
